import axiosConfig from '../utils/axiosConfig';

export interface InventoryWarehouse {
  _id: string;
  warehouse: {
    _id: string;
    name: string;
    location?: string;
  };
  warehouseName: string;
  quantity: number;
  lastUpdated: string;
}

export interface InventoryReportItem {
  _id: string;
  product: {
    _id: string;
    name: string;
    status: string;
  };
  productName: string;
  variantId: string;
  variantSerialNumber: number;
  variantAttributes: Record<string, string>;
  barcode: string;
  sku?: string;
  unit: string;
  categories: Array<{
    _id: string;
    name: string;
  }>;
  warehouseInventories: InventoryWarehouse[];
  createdAt: string;
  updatedAt: string;
}

export interface InventoryReportFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  warehouse?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  includeTotals?: boolean; // معامل اختياري لحساب الإجماليات
}

export interface InventoryReportResponse {
  success: boolean;
  data: InventoryReportItem[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
  totals?: {
    warehouses: { [key: string]: number };
    grandTotal: number;
  };
}

export interface Warehouse {
  _id: string;
  name: string;
  location?: string;
}

export interface Category {
  _id: string;
  name: string;
}

export interface WarehousesResponse {
  success: boolean;
  data: Warehouse[];
}

export interface CategoriesResponse {
  success: boolean;
  data: Category[];
}

class InventoryReportService {
  private baseURL = '/inventory-report';

  async getInventoryReport(filters: InventoryReportFilters = {}): Promise<InventoryReportResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.warehouse) params.append('warehouse', filters.warehouse);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.includeTotals !== undefined) params.append('includeTotals', filters.includeTotals.toString());

      console.log('جلب تقرير المخزون من:', `${this.baseURL}?${params.toString()}`);
      const response = await axiosConfig.get(`${this.baseURL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory report:', error);
      console.log('يحتاج إنشاء API endpoint في الخادم:', this.baseURL);
      throw error;
    }
  }

  async getTotals(filters: Omit<InventoryReportFilters, 'page' | 'limit' | 'sortBy' | 'sortOrder'> = {}): Promise<{ warehouses: { [key: string]: number }; grandTotal: number }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.warehouse) params.append('warehouse', filters.warehouse);

      console.log('🔄 جلب الإجماليات من:', `${this.baseURL}/totals?${params.toString()}`);
      const response = await axiosConfig.get(`${this.baseURL}/totals?${params.toString()}`);
      return response.data.totals;
    } catch (error) {
      console.error('Error fetching totals:', error);
      console.log('يحتاج إنشاء API endpoint في الخادم:', `${this.baseURL}/totals`);
      throw error;
    }
  }

  async getWarehouses(): Promise<WarehousesResponse> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/warehouses`);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      console.log('يحتاج إنشاء API endpoint في الخادم:', `${this.baseURL}/warehouses`);
      throw error;
    }
  }

  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/categories`);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      console.log('يحتاج إنشاء API endpoint في الخادم:', `${this.baseURL}/categories`);
      throw error;
    }
  }
}

const inventoryReportService = new InventoryReportService();
export default inventoryReportService;